import React, { type FC, type JSX, memo, useMemo } from 'react';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import type { ProductDatesAlternative } from '@/domainLogic/types/typesProduct.ts';

import {
  CARD_TYPE,
  resultClickedEvents,
} from '@/app/components/search/components/SearchPageContainer/resultClickedEvents.ts';
import type { DealCardCtaCallBackParams } from '@/app/components/search/components/Card/types.ts';
import { getAdultsChildrenCount } from '@/utils/peopleUtils.ts';
import { createNewSearchParams } from '@/app/components/search/common/reloadData/createNewSearchParams.ts';
import { SearchModes } from '@/domainLogic/index.ts';
import MyPriceBanner from '@/app/components/search/components/Banners/MyPriceBanner';
import { getIsApp } from '@/app/components/search/Utilities.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import LoyaltyBanner from '@/app/components/search/components/Banners/LoyaltyBanner';
import ReassuranceBanner from '@/app/components/search/components/Banners/ReassuranceBanner';
import { shouldShowLoyaltyBanner } from '@/app/common/Loyalty/utils.ts';
import { LOYALTY_CONTEXT, LOYALTY_POSITION } from '@/app/common/starTrack/events/utils/eventsUtils.ts';
import EvolutionCard from '@/app/components/search/components/EvolutionCard';
import ErrorBoundary from '@/app/common/components/ErrorBoundary';
import getSearchParams, { getSearchParamsClient } from '../../../../common/getSearchParams';
import clickEvent from '../../../../Curiosity/clickEvent';
import Adv from '../../../../../../common/biff/searchPage/AdvSearchPage';
import { CardsGridStyled } from './styled';
import type { DealCard } from '../../../../utils/buildDealCards/dealCardTypes';
import { CardsContext } from './CardsContext';
import Card from '../../../Card';
import type { RouteType } from '../../../../Curiosity';
import rtcoClientClickHotel from '../../../../rtco/rtcoClientClickHotel';
import { isLiveSearchForMemsearch } from '../../../../utils/api/utils';

export type DealCardWithAdv = {
  adv: boolean;
  advId?: number;
  hide?: boolean;
  key: string;
  dealCard: DealCard | undefined;
  isBanner?: boolean;
};
export type DealCardsWithAdv = DealCardWithAdv[];

export type CardsGridProps = {
  routeType: RouteType;
  cardsGridRef: React.MutableRefObject<HTMLDivElement | undefined>;
  isWaiting: boolean;
  isLoading: boolean;
  setCardOverHotelId?: React.Dispatch<React.SetStateAction<number | undefined>>;
};

const addEmptyCards = (): DealCardsWithAdv => {
  const array = new Array(3).fill(null);

  return array.map((_, i) => ({
    adv: false,
    key: `empty_card_${i}`,
    dealCard: undefined,
  }));
};

const decorateCards = (dealCards: DealCard[]): DealCardsWithAdv =>
  dealCards.map((dealCard, i) => ({
    adv: false,
    key: `results_card_${dealCard.product?.accommodation?.hotelId ?? `index_${i}`}`,
    dealCard,
  }));

const generateCards = (
  dealCards?: DealCard[],
  showMyPriceBanner?: boolean,
  showLoyaltyBanner?: boolean,
  showReassuranceBanner?: boolean
): DealCardsWithAdv => {
  const hasDeals = dealCards && Array.isArray(dealCards) && dealCards.length > 0;
  const dealCardsWithAdv: DealCardsWithAdv = hasDeals ? decorateCards(dealCards) : addEmptyCards();

  const adv1 = { adv: true, key: `adv-1`, advId: 1, position: showLoyaltyBanner ? 5 : 2, dealCard: undefined };
  const adv2 = { adv: true, key: `adv-2`, advId: 2, position: 12, dealCard: undefined };

  if (dealCardsWithAdv.length > 2) {
    dealCardsWithAdv.splice(adv1.position, 0, { ...adv1, hide: false });
  } else {
    dealCardsWithAdv.push({ ...adv1, hide: true });
  }

  if (dealCardsWithAdv.length > 12) {
    dealCardsWithAdv.splice(adv2.position, 0, { ...adv2, hide: false });
  } else {
    dealCardsWithAdv.push({ ...adv2, hide: true });
  }

  // Add MyPriceBanner after the first card if showMyPriceBanner is true
  if (showMyPriceBanner && dealCardsWithAdv && dealCardsWithAdv.length > 0) {
    dealCardsWithAdv.splice(1, 0, {
      adv: false,
      key: 'my-price-banner',
      dealCard: undefined,
      isBanner: true,
    });
  }

  // Add LoyaltyBanner instead of the first adv if loyaltyEnabled is true
  if (showLoyaltyBanner && dealCardsWithAdv && dealCardsWithAdv.length > 0) {
    dealCardsWithAdv.splice(2, 0, {
      adv: false,
      key: 'loyalty-banner',
      dealCard: undefined,
      isBanner: true,
    });
  }

  // Add ReassuranceBanner above the first card (between filters and first card)
  if (showReassuranceBanner && dealCardsWithAdv && dealCardsWithAdv.length > 0) {
    dealCardsWithAdv.splice(0, 0, {
      adv: false,
      key: 'reassurance-banner',
      dealCard: undefined,
      isBanner: true,
    });
  }

  return dealCardsWithAdv;
};

const shouldOpenInNewTab = ({ searchParams }: { searchParams: SearchParams }) => {
  const isApp = getIsApp(searchParams);

  if (isApp) return false;
  return true;
};

const CardsGrid: FC<CardsGridProps> = memo(
  ({ routeType, cardsGridRef, isWaiting, isLoading, setCardOverHotelId }): JSX.Element | null => {
    const {
      searchInfoData,
      searchParams: searchParamsServer,
      localization,
      curiosityParams,
      commonData,
    } = usePageWrapperData();
    const { productsData } = useSearchPageData();
    const { handleHotelChosen, starTrack, handleSearchChanged } = useCommonClient();
    const { isWhiteLabel } = localization;
    const { authResponse } = commonData;
    const { vcSearchId } = productsData;
    const { searchMode, isLiveSearch } = searchInfoData;
    const searchParams = getSearchParamsClient(searchParamsServer);
    const travellersCount = getAdultsChildrenCount(searchParams);
    const isHO = searchMode === SearchModes.HO;
    const myPriceBannerEnabled = localization?.configValuesApp?.myPriceBannerEnabled;
    const loyaltyEnabled = localization?.configValuesApp?.loyaltyEnabled;
    const isApp = getIsApp(searchParams);
    const isLoggedIn = authResponse?.loggedIn;
    // only show loyalty banner for HO
    const showLoyaltyBanner = isHO && shouldShowLoyaltyBanner(commonData, localization, isApp);
    const showMyPriceBanner = myPriceBannerEnabled && isHO && isLiveSearch && !isLoggedIn && !loyaltyEnabled;
    const evolutionResultsOnlyEnabled = localization?.configValuesApp?.evolutionResultsOnlyEnabled;
    const reassuranceBannerEnabled = localization?.configValuesApp?.reassuranceBannerEnabled;
    const showReassuranceBanner = evolutionResultsOnlyEnabled && reassuranceBannerEnabled;
    const dealCardsWithAdv = generateCards(
      productsData?.dealCards,
      showMyPriceBanner,
      showLoyaltyBanner,
      showReassuranceBanner
    );
    const openInNewTab = shouldOpenInNewTab({
      searchParams,
    });

    const dealCardCtaCallBack = ({
      e,
      dealCard,
      payload,
      selectedAlternativeDatesIndex,
    }: DealCardCtaCallBackParams) => {
      if (payload) {
        clickEvent({ clickParams: payload, routeType, commonData, localization, curiosityParams });
      }

      if (dealCard?.product?.dates?.alternative) {
        if (selectedAlternativeDatesIndex !== undefined) {
          const searchParamsFresh = getSearchParams();
          const { dateFrom, dateTo } = (dealCard.product.dates.alternative as ProductDatesAlternative[])[
            selectedAlternativeDatesIndex || 0
          ];
          searchParamsFresh[URLParameters.DATE_FROM] = dateFrom;
          searchParamsFresh[URLParameters.DATE_TO] = dateTo;

          const newSearchParams = createNewSearchParams({ searchParams: searchParamsFresh });
          handleSearchChanged({ newSearchParams, isAlternativeDates: true });
        }
      } else if (e) {
        if (isLiveSearchForMemsearch({ searchInfoData })) {
          rtcoClientClickHotel({
            searchParams: getSearchParams(),
            searchInfoData,
            localization,
            hotelId: dealCard?.product.accommodation.hotelId || 0,
            commonData,
          });
        }
        const { clickId } = handleHotelChosen(`${dealCard?.product?.accommodation?.hotelId || ''}`, {
          openInNewTab,
          ...(dealCard ? { dealCard } : {}),
        });
        resultClickedEvents({
          starTrack,
          cardType: CARD_TYPE.LIST,
          dealCard,
          clickId,
          vcSearchId,
          commonData,
          curiosityParams,
          localization,
        });
      }
    };

    const value = useMemo(
      () => ({
        travellersCount,
        searchParams,
        isWaiting,
        isLoading,
        isWhiteLabel,
      }),
      [travellersCount, searchParams, isWaiting, isLoading, isWhiteLabel]
    );

    return (
      <CardsContext.Provider value={value}>
        <CardsGridStyled ref={cardsGridRef}>
          {dealCardsWithAdv?.map((dealCardWithAdv) => {
            if (dealCardWithAdv.adv) {
              return (
                <Adv
                  id={`div-gpt-ad-intrip${dealCardWithAdv.advId}`}
                  isIntraList={true}
                  key={dealCardWithAdv.key}
                  hide={dealCardWithAdv.hide}
                />
              );
            }
            if (dealCardWithAdv.isBanner) {
              if (dealCardWithAdv.key === 'loyalty-banner') {
                return (
                  <ErrorBoundary key={dealCardWithAdv.key} id="Results/LoyaltyBanner">
                    <LoyaltyBanner
                      loyaltyContext={LOYALTY_CONTEXT.RESULT}
                      loyaltyPosition={LOYALTY_POSITION.TOP}
                      isLoggedIn={isLoggedIn}
                    />
                  </ErrorBoundary>
                );
              }
              if (dealCardWithAdv.key === 'my-price-banner') {
                // TODO: Should it be dismissed?
                return (
                  <ErrorBoundary key={dealCardWithAdv.key} id="Results/MyPriceBanner">
                    <MyPriceBanner />
                  </ErrorBoundary>
                );
              }
              if (dealCardWithAdv.key === 'reassurance-banner') {
                return (
                  <ErrorBoundary key={dealCardWithAdv.key} id="Results/ReassuranceBanner">
                    <ReassuranceBanner />
                  </ErrorBoundary>
                );
              }
            }

            return evolutionResultsOnlyEnabled ? (
              <ErrorBoundary key={dealCardWithAdv.key} id="EvolutionCard">
                <EvolutionCard
                  dealCard={dealCardWithAdv.dealCard}
                  isOverlay={false}
                  setCardOverHotelId={setCardOverHotelId}
                  dealCardCtaCallBack={dealCardCtaCallBack}
                />
              </ErrorBoundary>
            ) : (
              <ErrorBoundary key={dealCardWithAdv.key} id="Card">
                <Card
                  dealCard={dealCardWithAdv.dealCard}
                  isOverlay={false}
                  setCardOverHotelId={setCardOverHotelId}
                  dealCardCtaCallBack={dealCardCtaCallBack}
                />
              </ErrorBoundary>
            );
          })}
        </CardsGridStyled>
      </CardsContext.Provider>
    );
  },
  (prevProps: CardsGridProps, nextProps: CardsGridProps) => {
    if (prevProps.isWaiting !== nextProps.isWaiting) return false;
    if (prevProps.isLoading !== nextProps.isLoading) return false;
    return true;
  }
);

CardsGrid.displayName = 'CardsGrid';

export default CardsGrid;
